import React from "react"
import PropTypes from "prop-types"
import { Field, reduxForm } from "redux-form"
import groupBy from "ramda/es/groupBy"
import prop from "ramda/es/prop"
import sortBy from "ramda/es/sortBy"
import compose from "ramda/es/compose"
import splitEvery from "ramda/es/splitEvery"
import map from "ramda/es/map"
import { Box } from "@infotech/uikit-grid"
import Fieldset from "@infotech/uikit-fieldset"
import Input from "@infotech/uikit-input"
import Checkbox from "@infotech/uikit-checkbox"
import { Subtitle1 } from "@infotech/uikit-text"
import hint from "@infotech/uikit-hint"
import { fieldWrapper } from "src/engine/modules/reduxForm"
import { FORM_NAME } from "src/engine/modules/rolesForm"
import hocInterface from "src/helpers/hocInterface"

const CheckboxRF = fieldWrapper(Checkbox)
const BoxWithHint = hint(Box)

const PermissionGroup = ({ title, options }) => (
  <Box w="50%" p="2md">
    <Box loose pb="1md">
      <Subtitle1
        style={{
          overflow: `hidden`,
          whiteSpace: `nowrap`,
          textOverflow: `ellipsis`,
        }}
      >
        {title}
      </Subtitle1>
    </Box>
    <Box loose>
      {options.map(({ id, label, hint }, index) => (
        <BoxWithHint
          loose
          hint={hint}
          hintConfig={{
            placement: `top-left`,
            on: `mouseenter`,
            hideOn: `leave`,
          }}
        >
          <Field
            key={id}
            name={id}
            component={CheckboxRF}
            type="checkbox"
            props={{
              label,
              ...(index === options.length - 1
                ? null
                : { labelProps: { style: { marginBottom: 8 } } }),
            }}
          />
        </BoxWithHint>
      ))}
    </Box>
  </Box>
)

PermissionGroup.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      hint: PropTypes.string.isRequired,
    }),
  ).isRequired,
}

const mapPermissionsToOptions = map(permission => ({
  id: permission.key,
  label: permission.title,
  hint: permission.description,
}))

const dotsToDashes = string => string.replace(/\./g, `-`)
// Заменяем точки на тире и наоборот, потому что redux-form считает точки вложенностью
const fixDots = permissions =>
  permissions.map(permission => ({
    ...permission,
    key: dotsToDashes(permission.key),
  }))

const preparePermissions = compose(
  splitEvery(2),
  map(sortBy(prop(`position`))),
  Object.values,
  groupBy(prop(`permissionGroupId`)),
  fixDots,
)

const RolesForm = ({ roleName, permissions }) => (
  <Box>
    <Box pl="2md" pt="2md" pr="2md">
      <Fieldset caption="Наименование роли">
        <Input value={roleName} disabled />
      </Fieldset>
    </Box>
    <Box>
      {preparePermissions(permissions).map(pair => (
        <Box flex bb key={pair[0][0].key}>
          {pair.map(permissionGroup => (
            <PermissionGroup
              key={permissionGroup[0].key}
              title={permissionGroup[0].permissionGroupTitle}
              options={mapPermissionsToOptions(permissionGroup)}
            />
          ))}
        </Box>
      ))}
    </Box>
  </Box>
)

RolesForm.propTypes = {
  roleName: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      permissionGroupId: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired,
      permissionGroupTitle: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,
}

const enhancer = hocInterface(
  {
    consumeProps: {
      // For the redux-form
      onCancel: PropTypes.func.isRequired,
      onSubmit: PropTypes.func.isRequired,
    },
  },
  reduxForm({
    form: FORM_NAME,
  }),
)

export default enhancer(RolesForm)
